import { useState, useCallback } from "react";
import { useLazyQuery } from "@apollo/client";

import { readToken } from "services/localStorage.service";
import { KeywordSuggester } from "gql/boolean.gql";
import { useAppSelector } from "./reduxHooks";

interface UseKeywordSuggesterResult {
  keywords: string[] | undefined;
  suggestedJobTitles: string[] | undefined;
  loading: boolean;
  refreshLoading: boolean;
  error: Error | undefined;
  fetchKeywords: (keywords: string[]) => void;
  refreshKeywords: () => void;
}

export const useKeywordSuggester = (): UseKeywordSuggesterResult => {
  const [keywords, setKeywords] = useState<string[]>([]);
  const [refreshLoading, setRefreshLoading] = useState<boolean>(false);

  const jobTitle = useAppSelector((state: any) => state.search?.job_titles);
  const [fetchKeywords, { loading, data, error, refetch }] = useLazyQuery(KeywordSuggester, {
    variables: { jobTitles: [], token: readToken(), keywords: [] },
    fetchPolicy: "network-only",
  });

  const refreshKeywords = useCallback(async () => {
    try {
      setRefreshLoading(true);
      await refetch({ jobTitles: jobTitle, token: readToken(), keywords });
    } catch (error) {
      console.error("Error refetching keywords:", error);
    } finally {
      setRefreshLoading(false);
    }
  }, [jobTitle, refetch, keywords]);

  return {
    keywords: data?.jobTitlesAndKeywordsSuggestor?.keywords ?? [],
    suggestedJobTitles: data?.jobTitlesAndKeywordsSuggestor?.jobTitles ?? [],
    loading,
    refreshLoading,
    error,
    fetchKeywords: (newKeywords: string[]) => {
      setKeywords(newKeywords);
      fetchKeywords({ variables: { jobTitles: jobTitle, token: readToken(), keywords: newKeywords } });
    },
    refreshKeywords,
  };
};
